import {styled} from '@linaria/react';
import React, {ComponentProps, CSSProperties, FC, ReactNode} from 'react';
import {
  desktopAndUpLinaria,
  extraLargeScreensLinaria,
  higherDesktopAndUpLinaria,
  largeScreensLinaria,
  tabletAndUpLinaria,
} from '../../../../constants/customMedia';
import {RADIUS, SIZE, SPACING} from '../../../../constants/sizing';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../../constants/typography';
import {useGetAriaLabelFromUrl} from '../../../../hooks/useGetAriaLabelFromUrl/useGetAriaLabelFromUrl';
import {LinkType} from '../../../../types/Link';
import {Button} from '../../../atoms/Buttons/Button';
import {Image} from '../../../atoms/Image/Image';
import {Text} from '../../../atoms/Text/Text';
import {UnstyledLink} from '../../../atoms/UnstyledLink/UnstyledLink';
import {SliderHookResponse} from '../../../molecules/HorizontalSlider/types';
import {ProductImage} from '../../../molecules/ProductImage/ProductImage';
import {SliderPaginationDots} from '../../SliderPaginationDots/SliderPaginationDots';
import {AUTO_SLIDE_DURATION_IN_SECS} from '../constants';
import {SPOTLIGHT_CARD_GLOWS_DESKTOP} from '../glows';
import {SpotlightSlideBackground} from './SpotlightSlideBackground';
import {SpotlightColorPair, SpotlightSlideBaseProps} from './types';

export interface SpotlightCategoryLink {
  label: string;
  linkTarget: LinkType;
}

export interface SpotlightSlidePanelProps extends SpotlightSlideBaseProps {
  /** Desktop-only category buttons, rendered in a 3×2 grid over the right panel. */
  categoryLinks?: SpotlightCategoryLink[];
  /**
   * Fill + label colors for the *solid* category-button style (used when there
   * is no media). Ignored over media, where buttons switch to a fixed glass
   * style.
   */
  categoryLinkColors?: SpotlightColorPair;
  /** Slider navigation (‹ ›) slot, rendered bottom-right of the panel. */
  navigation?: ReactNode;
}

interface CategoryButtonsProps {
  categoryLinks?: SpotlightCategoryLink[];
  categoryLinkColors?: SpotlightColorPair;
  hasMedia: boolean;
}

/**
 * Desktop 3×2 category button grid. Over media the buttons use a fixed glass
 * style; with no media they use the solid `primary` variant tinted by
 * `categoryLinkColors`. Renders nothing when there are no links.
 */
const CategoryButtons: FC<CategoryButtonsProps> = ({
  categoryLinks,
  categoryLinkColors,
  hasMedia,
}) => {
  if (!categoryLinks || categoryLinks.length === 0) return null;

  return (
    <CategoryGrid data-testid="spotlight-category-links">
      {categoryLinks.map(category => (
        <Button
          key={category.label}
          href={category.linkTarget.url}
          // Over media: fixed glass style, ignoring categoryLinkColors.
          // No media: solid style using the `primary` variant — its
          // transparent resting border keeps the fill clean, while its
          // built-in focus-visible ring survives the inline fill/label
          // override (a `secondary` border would be flattened to
          // transparent by that override, leaving no visible focus).
          variant={hasMedia ? 'glassy' : 'primary'}
          size="lg"
          style={
            hasMedia || !categoryLinkColors
              ? undefined
              : ({
                  '--background-color': categoryLinkColors.backgroundColor,
                  '--color': categoryLinkColors.foregroundColor,
                } as CSSProperties)
          }
          onClick={e => {
            e.stopPropagation();
          }}
        >
          {category.label}
        </Button>
      ))}
    </CategoryGrid>
  );
};

interface SlidePaginationComponentProps
  extends Omit<
    ComponentProps<typeof SliderPaginationDots>,
    'scrollToSlide' | 'currentSlide'
  > {
  currentSlide: SliderHookResponse['currentSlide'];
  scrollToSlide?: SliderHookResponse['scrollToSlide'];
  onStopAutomaticSlide: VoidFunction;
  foregroundColor: string;
}

/**
 * In-slide pagination — same wiring as the legacy desktop `Slide` shell. Dots
 * are tinted by `foregroundColor` so they stay readable on light and dark
 * palettes (legacy always used white because that shell sat on dark media).
 * Renders nothing for single-slide (or incomplete) slider state.
 */
const SlidePaginationComponent: FC<SlidePaginationComponentProps> = ({
  currentSlide,
  scrollToSlide,
  maxPages,
  isAnimationStopped,
  isAnimationPaused,
  onStopAutomaticSlide,
  foregroundColor,
}) => {
  if (maxPages <= 1 || !scrollToSlide) return null;

  const handleScrollToSlide = (index: number) => {
    onStopAutomaticSlide();
    scrollToSlide(index);
  };

  return (
    <PaginationSlot data-testid="spotlight-pagination">
      <StyledSliderPaginationDots
        isAnimationStopped={isAnimationStopped}
        isAnimationPaused={isAnimationPaused}
        maxPages={maxPages}
        currentSlide={currentSlide}
        scrollToSlide={handleScrollToSlide}
        enlargeActiveBullet
        autoplayDuration={AUTO_SLIDE_DURATION_IN_SECS}
        dotsColor={{
          active: `${foregroundColor}66`,
          inactive: `${foregroundColor}66`,
          progress: foregroundColor,
        }}
      />
    </PaginationSlot>
  );
};

interface PanelLeftColumnProps {
  foregroundColor: string;
  badge?: ReactNode;
  topline?: string;
  headline?: string;
  hasMedia: boolean;
  claim: ReactNode;
  productImages?: SpotlightSlideBaseProps['productImages'];
  currentSlide: number;
  maxPages: number;
  scrollToSlide?: SpotlightSlideBaseProps['scrollToSlide'];
  isAnimationStopped: boolean;
  isAnimationPaused: boolean;
  onStopAutomaticSlide: VoidFunction;
  uniqueKey: string;
}

const PanelLeftColumn: FC<PanelLeftColumnProps> = ({
  foregroundColor,
  badge,
  topline,
  headline,
  hasMedia,
  claim,
  productImages,
  currentSlide,
  maxPages,
  scrollToSlide,
  isAnimationStopped,
  isAnimationPaused,
  onStopAutomaticSlide,
  uniqueKey,
}) => (
  <LeftColumn style={{color: foregroundColor} as CSSProperties}>
    <TopContent>
      {badge && <BadgeSlot data-testid="spotlight-badge">{badge}</BadgeSlot>}
      {(topline || headline) && (
        <Headlines>
          {topline && <Topline>{topline}</Topline>}
          {headline && (
            <Headline data-testid="spotlight-headline">{headline}</Headline>
          )}
        </Headlines>
      )}
      {hasMedia && claim}
      <SlidePaginationComponent
        currentSlide={currentSlide}
        maxPages={maxPages}
        scrollToSlide={scrollToSlide}
        isAnimationStopped={isAnimationStopped}
        isAnimationPaused={isAnimationPaused}
        onStopAutomaticSlide={onStopAutomaticSlide}
        foregroundColor={foregroundColor}
        key={uniqueKey}
      />
    </TopContent>
    {productImages && productImages.length > 0 && (
      <ProductsWrapper>
        {productImages.map((product, index) => (
          <StyledProductImage
            src={product.url}
            widths={[120, 240, 360]}
            key={`spotlight-slide-product-${index}`}
            alt={product.description}
            increasePadding
            padded
          />
        ))}
      </ProductsWrapper>
    )}
  </LeftColumn>
);

/**
 * Desktop Spotlight slide shell — a split banner over the ambient background.
 *
 * The banner itself paints no full-bleed surface: base colour + glow live on
 * the ambient `SpotlightSlideAmbientBackground` behind the slides. The left
 * column sits directly on the ambient; the right panel floats over it with its
 * own rounded surface (solid colour, or hero media when present). Colour-only
 * slides additionally paint `glowColors` blooms on the panel itself so they
 * roughly overlap the ambient ones like Figma.
 *
 * Left column: badge + topline + headline (+ the text claim when media is
 * present) + pagination + product packshots, all in `foregroundColor`.
 * Right panel: the on-card content centered (the text claim tinted by
 * `cardContent.color`, or a centered brand logo), the sponsored slot (top-left),
 * the info icon (top-right), the category buttons, and the slider arrows.
 */
export const SpotlightSlidePanel: FC<SpotlightSlidePanelProps> = ({
  backgroundColor,
  glowColors,
  media,
  cardContent,
  headline,
  topline,
  foregroundColor,
  productImages,
  badge,
  infoIcon,
  sponsored,
  bannerTarget,
  isCloseToViewport,
  onSlideClick,
  onStopAutomaticSlide,
  currentSlide = 0,
  maxPages = 0,
  scrollToSlide,
  isAnimationStopped,
  isAnimationPaused,
  uniqueKey,
  categoryLinks,
  categoryLinkColors,
  navigation,
  className,
  'data-testid': dataTestid = 'spotlightSlidePanel',
}) => {
  const noop = () => undefined;
  const getAriaLabelFromUrl = useGetAriaLabelFromUrl();
  const hasMedia = Boolean(media?.src);

  const textContent = cardContent?.kind === 'text' ? cardContent : undefined;
  const logoContent = cardContent?.kind === 'logo' ? cardContent : undefined;

  const claim = textContent && (
    <ClaimWrapper style={{color: textContent.color} as CSSProperties}>
      <ClaimText data-testid="spotlight-card-text">
        {textContent.headline}
      </ClaimText>
      {textContent.subline && (
        <ClaimSubline data-testid="spotlight-card-subline">
          {textContent.subline}
        </ClaimSubline>
      )}
    </ClaimWrapper>
  );

  const logo = logoContent && (
    <LogoWrapper data-testid="spotlight-card-logo">
      <LogoImage
        src={logoContent.image.url}
        alt={logoContent.image.description}
        loading="lazy"
        breakpoints={[]}
        ratio="2:1"
        widths={[400]}
        disableFixedRatio
      />
    </LogoWrapper>
  );

  return (
    <Wrapper
      className={className}
      data-testid={dataTestid}
      onClick={onSlideClick}
    >
      <PanelLeftColumn
        foregroundColor={foregroundColor}
        badge={badge}
        topline={topline}
        headline={headline}
        hasMedia={hasMedia}
        claim={claim}
        productImages={productImages}
        currentSlide={currentSlide}
        maxPages={maxPages}
        scrollToSlide={scrollToSlide}
        isAnimationStopped={isAnimationStopped ?? true}
        isAnimationPaused={isAnimationPaused ?? true}
        onStopAutomaticSlide={onStopAutomaticSlide ?? noop}
        uniqueKey={uniqueKey}
      />

      <RightPanel data-testid="spotlight-right-panel">
        <SpotlightSlideBackground
          backgroundColor={backgroundColor}
          glows={SPOTLIGHT_CARD_GLOWS_DESKTOP}
          glowColors={glowColors}
          media={media}
          mediaLoading={isCloseToViewport ? 'eager' : 'lazy'}
        >
          <RightForeground>
            {sponsored && (
              <SponsoredSlot data-testid="spotlight-sponsored">
                {sponsored}
              </SponsoredSlot>
            )}
            {infoIcon && (
              <InfoIconSlot
                data-testid="spotlight-info-icon"
                style={
                  textContent
                    ? ({color: textContent.color} as CSSProperties)
                    : undefined
                }
              >
                {infoIcon}
              </InfoIconSlot>
            )}
            {logo}
            {!hasMedia && claim}
            <CategoryButtons
              categoryLinks={categoryLinks}
              categoryLinkColors={categoryLinkColors}
              hasMedia={hasMedia}
            />
            {navigation && <NavigationSlot>{navigation}</NavigationSlot>}
          </RightForeground>
        </SpotlightSlideBackground>
      </RightPanel>

      <Link
        data-testid="bannerTarget"
        aria-label={getAriaLabelFromUrl(bannerTarget.url)}
        to={bannerTarget.url}
        onClick={e => {
          e.preventDefault();
          bannerTarget.onClick?.();
        }}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  height: 100%;
  position: relative;
`;

const Link = styled(UnstyledLink)`
  position: absolute;
  inset: 0;
  z-index: 1;
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-basis: 34%;
  width: 34%;
  padding: ${SPACING['Y.Xl2']} 30px;
  position: relative;
  z-index: 2;
  pointer-events: none;

  ${tabletAndUpLinaria(`
    padding: ${SPACING['Y.Lg']} ${SIZE.U5};
  `)}

  ${desktopAndUpLinaria(`
    padding: ${SPACING['Y.Lg']} 30px;
  `)}

  ${higherDesktopAndUpLinaria(`
    padding: ${SPACING['Y.Xl2']} ${SIZE.U15};
  `)}
`;

const TopContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING['Y.Lg']};
`;

const BadgeSlot = styled.div`
  pointer-events: auto;
  align-self: flex-start;
`;

const PaginationSlot = styled.div`
  pointer-events: auto;
  align-self: flex-start;
  position: relative;
  z-index: 3;
`;

const StyledSliderPaginationDots = styled(SliderPaginationDots)`
  justify-content: start;
  margin: 0;
  position: relative;
  z-index: 3;
`;

const Headlines = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING['Y.Sm']};
`;

const Topline = styled(Text)`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.w500};
  line-height: 1.5;
  text-transform: uppercase;
  letter-spacing: 0.64px;
  color: inherit;

  ${largeScreensLinaria(`
    font-size: ${FONT_SIZES.px16};
  `)}
`;

const Headline = styled(Text)`
  font-size: ${FONT_SIZES.px20};
  font-weight: ${FONT_WEIGHTS.w500};
  line-height: 1.1;
  color: inherit;

  ${higherDesktopAndUpLinaria(`
    font-size: ${FONT_SIZES.px32};
  `)}

  ${extraLargeScreensLinaria(`
    font-size: ${FONT_SIZES.px40};
  `)}
`;

const ProductsWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING['X.Sm']};
  margin-top: ${SPACING['Y.Xl']};
`;

const StyledProductImage = styled(ProductImage)`
  flex: 1 1 0;
  min-width: 0;
  height: auto;
  aspect-ratio: 3 / 4;
  padding-top: 0;

  &&& img {
    padding: 14% 12%;
  }
`;

const RightPanel = styled.div`
  position: relative;
  flex: 1;
  margin: ${SPACING['Y.Lg']} ${SPACING['X.Lg']} ${SPACING['Y.Lg']} 0;
  border-radius: ${RADIUS.Md};
  overflow: hidden;
  z-index: 2;
  pointer-events: none;
  box-shadow: 0 0 ${SIZE.U6} 0 rgba(60, 0, 41, 0.24);
`;

const RightForeground = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${SPACING['Y.Xl']};
  padding: ${SPACING['Y.Xl']};
  text-align: center;
`;

const SponsoredSlot = styled.div`
  position: absolute;
  top: ${SPACING['Y.Lg']};
  left: ${SPACING['X.Lg']};
  pointer-events: auto;
`;

const InfoIconSlot = styled.div`
  position: absolute;
  top: ${SPACING['Y.Lg']};
  right: ${SPACING['X.Lg']};
  pointer-events: auto;
`;

const NavigationSlot = styled.div`
  position: absolute;
  bottom: ${SPACING['Y.Lg']};
  right: ${SPACING['X.Lg']};
  pointer-events: auto;
`;

const ClaimWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING['Y.Md']};
  color: inherit;
`;

const ClaimText = styled(Text)`
  font-size: ${FONT_SIZES.px32};
  font-weight: ${FONT_WEIGHTS.w500};
  line-height: 1.1;
  text-align: center;
  color: inherit;

  ${largeScreensLinaria(`
    font-size: ${FONT_SIZES.px46};
  `)}

  ${extraLargeScreensLinaria(`
    font-size: ${FONT_SIZES.px56};
  `)}
`;

const ClaimSubline = styled(Text)`
  font-size: ${FONT_SIZES.px16};
  font-weight: ${FONT_WEIGHTS.w400};
  line-height: 1.3;
  text-align: center;
  color: inherit;

  ${largeScreensLinaria(`
    font-size: ${FONT_SIZES.px20};
  `)}
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
`;

const LogoImage = styled(Image)`
  max-width: 60%;
  max-height: ${SIZE.U40};
  width: auto;
  height: auto;
  object-fit: contain;
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${SPACING['X.Md']};
  width: 100%;
  max-width: 620px;
  pointer-events: auto;
  z-index: 2;
`;
